import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useConnectionStore } from '@/stores/connectionStore';
import { useBrowserStore } from '@/stores/browserStore';
import { CheckCircle, AlertCircle, X, ExternalLink, Wifi, WifiOff } from 'lucide-react';

type OAuthStatus = { type: 'success'; account: string; purpose?: string } | { type: 'error'; message: string } | null;

export function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [oauthStatus, setOauthStatus] = useState<OAuthStatus>(null);
  const {
    connections,
    checkConnection,
    getZoomConnectUrl,
    disconnect,
    capabilities,
    fetchCapabilities,
  } = useConnectionStore();
  const browserConnected = useBrowserStore((s) => s.extensionConnected);
  const startPolling = useBrowserStore((s) => s.startPolling);
  const stopPolling = useBrowserStore((s) => s.stopPolling);

  // Fetch connection state on mount
  useEffect(() => {
    checkConnection();
    fetchCapabilities();
    startPolling();
    return () => stopPolling();
  }, [checkConnection, fetchCapabilities, startPolling, stopPolling]);

  // Handle OAuth callback query params
  useEffect(() => {
    const oauth = searchParams.get('oauth');
    const account = searchParams.get('account');
    const message = searchParams.get('message');
    const purpose = searchParams.get('purpose');

    if (oauth === 'success' && account) {
      setOauthStatus({ type: 'success', account, purpose: purpose ?? undefined });
      setSearchParams({}, { replace: true });
      // Refresh connection state
      checkConnection();
      fetchCapabilities();
      const timer = setTimeout(() => setOauthStatus(null), 5000);
      return () => clearTimeout(timer);
    } else if (oauth === 'error') {
      setOauthStatus({ type: 'error', message: message ?? 'OAuth connection failed' });
      setSearchParams({}, { replace: true });
      const timer = setTimeout(() => setOauthStatus(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [searchParams, setSearchParams, checkConnection, fetchCapabilities]);

  const handleConnect = useCallback(async (purpose: 'reader' | 'writer') => {
    const url = await getZoomConnectUrl('/settings', purpose);
    if (url) {
      window.location.href = url;
    }
  }, [getZoomConnectUrl]);

  const handleDisconnect = useCallback(async (accountId: string) => {
    if (confirm('Are you sure you want to disconnect this account?')) {
      await disconnect(accountId);
      await fetchCapabilities();
    }
  }, [disconnect, fetchCapabilities]);

  // Find reader and writer connections
  const readerConnection = connections.find(
    (c) => c.credentialPurpose === 'reader' || c.credentialPurpose === 'general'
  );
  const writerConnection = connections.find(
    (c) => c.credentialPurpose === 'writer'
  );

  const canRead = capabilities?.canRead ?? !!readerConnection;
  const canWrite = capabilities?.canWrite ?? !!writerConnection;

  return (
    <div className="flex-1 overflow-y-auto">
      {/* OAuth status banner */}
      {oauthStatus?.type === 'success' && (
        <div className="bg-green-900/30 border-b border-green-700/50 px-4 py-2">
          <div className="mx-auto max-w-3xl flex items-center justify-between">
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle className="h-4 w-4" />
              <span className="text-sm">
                Successfully connected {oauthStatus.purpose === 'writer' ? 'write access' : 'read access'} for {oauthStatus.account}
              </span>
            </div>
            <button onClick={() => setOauthStatus(null)} className="text-green-400/70 hover:text-green-400">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {oauthStatus?.type === 'error' && (
        <div className="bg-red-900/30 border-b border-red-700/50 px-4 py-2">
          <div className="mx-auto max-w-3xl flex items-center justify-between">
            <div className="flex items-center gap-2 text-red-400">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">OAuth error: {oauthStatus.message}</span>
            </div>
            <button onClick={() => setOauthStatus(null)} className="text-red-400/70 hover:text-red-400">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-3xl px-4 py-8">
        <h1 className="text-2xl font-semibold text-foreground mb-2">Settings</h1>
        <p className="text-muted-foreground mb-8">Manage your Zoom connections and integrations.</p>

        <div className="space-y-4">
          {/* Zoom Read Access Card */}
          <ConnectionCard
            title="Zoom Read Access"
            description="Read account data, users, settings, and configuration. Required for all account queries."
            connected={canRead}
            connection={readerConnection}
            onConnect={() => handleConnect('reader')}
            onDisconnect={readerConnection ? () => handleDisconnect(readerConnection.accountId) : undefined}
          />

          {/* Zoom Write Access Card */}
          <ConnectionCard
            title="Zoom Write Access"
            description="Create and modify resources like queues, users, and settings. Required for automated configuration changes via API."
            connected={canWrite}
            connection={writerConnection}
            onConnect={() => handleConnect('writer')}
            onDisconnect={writerConnection ? () => handleDisconnect(writerConnection.accountId) : undefined}
          />

          {/* Browser Extension Card */}
          <div className={`rounded-lg border p-5 ${browserConnected ? 'border-green-700/50 bg-green-900/10' : 'border-border bg-card'}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className={`mt-0.5 rounded-full p-1.5 ${browserConnected ? 'bg-green-900/30 text-green-400' : 'bg-secondary text-muted-foreground'}`}>
                  {browserConnected ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
                </div>
                <div>
                  <h3 className="font-medium text-foreground">Browser Extension</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Automate Zoom admin UI actions that aren't available via API. Used for advanced configuration like Contact Center settings.
                  </p>
                  {browserConnected ? (
                    <p className="text-sm text-green-400 mt-2">Connected</p>
                  ) : (
                    <p className="text-sm text-muted-foreground mt-2">Not connected</p>
                  )}
                </div>
              </div>
              {!browserConnected && (
                <a
                  href="/browser"
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  Setup
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Connection Card Component
// =============================================================================

interface ConnectionCardProps {
  title: string;
  description: string;
  connected: boolean;
  connection?: { accountName: string; connectedAt: string; accountId: string };
  onConnect: () => void;
  onDisconnect?: () => void;
}

function ConnectionCard({ title, description, connected, connection, onConnect, onDisconnect }: ConnectionCardProps) {
  return (
    <div className={`rounded-lg border p-5 ${connected ? 'border-green-700/50 bg-green-900/10' : 'border-border bg-card'}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className={`mt-0.5 rounded-full p-1.5 ${connected ? 'bg-green-900/30 text-green-400' : 'bg-secondary text-muted-foreground'}`}>
            {connected ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          </div>
          <div>
            <h3 className="font-medium text-foreground">{title}</h3>
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
            {connected && connection ? (
              <div className="mt-2 text-sm">
                <p className="text-green-400">{connection.accountName}</p>
                <p className="text-muted-foreground">
                  Connected {new Date(connection.connectedAt).toLocaleDateString()}
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mt-2">Not connected</p>
            )}
          </div>
        </div>
        <div className="shrink-0">
          {connected && onDisconnect ? (
            <button
              onClick={onDisconnect}
              className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
            >
              Disconnect
            </button>
          ) : !connected ? (
            <button
              onClick={onConnect}
              className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Connect
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
